import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { getProductBySlug } from "./data/products";
import SiteNav from "./components/SiteNav";
import usePageTitle from "./usePageTitle";

/** Horizontal drag distance (px) that counts as a swipe between gallery photos. */
const SWIPE_THRESHOLD_PX = 40;

/** 240px square thumbnail generated beside each product photo by scripts/make-thumbs.sh. */
const thumbSrc = (src) => src.replace(/\.jpe?g$/i, ".thumb.jpeg");

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const gallery = useMemo(
    () => (product?.images?.length ? product.images : product?.image ? [product.image] : []),
    [product],
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const thumbsRef = useRef(null);
  const swipeStart = useRef(null);
  usePageTitle(product?.name);

  useEffect(() => {
    setActiveIndex(0);
  }, [slug]);

  // Keep the active thumbnail visible inside the horizontally scrolling strip on phones.
  useEffect(() => {
    const strip = thumbsRef.current;
    const active = strip?.children[activeIndex];
    if (!strip || !active || strip.scrollWidth <= strip.clientWidth) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    active.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest", inline: "center" });
  }, [activeIndex]);

  // Warm the neighbouring photos so swiping/arrowing never waits on the network.
  useEffect(() => {
    if (gallery.length < 2) return;
    [activeIndex + 1, activeIndex - 1].forEach((i) => {
      const img = new Image();
      img.src = gallery[(i + gallery.length) % gallery.length];
    });
  }, [activeIndex, gallery]);

  if (!product) {
    return <Navigate to="/collection" replace />;
  }

  const count = gallery.length;
  const activeImage = gallery[activeIndex] ?? product.image;
  const step = (delta) => setActiveIndex((i) => (i + delta + count) % count);

  const onPointerDown = (e) => {
    if (count < 2) return;
    swipeStart.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    // Ignore mostly-vertical gestures so page scrolling is unaffected.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) < Math.abs(dy)) return;
    step(dx < 0 ? 1 : -1);
  };
  const onKeyDown = (e) => {
    if (count < 2) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    }
  };

  return (
    <div className="collection-page product-detail-page">
      <SiteNav />

      <section className="product-detail">
        <Link to="/collection" className="product-detail__back">
          ← Back to collection
        </Link>

        <div className="product-detail__layout">
          <div className="product-detail__gallery reveal visible">
            <div
              className={`product-detail__main${count > 1 ? " product-detail__main--swipeable" : ""}`}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerCancel={() => { swipeStart.current = null; }}
              onKeyDown={onKeyDown}
              tabIndex={count > 1 ? 0 : undefined}
              role={count > 1 ? "group" : undefined}
              aria-roledescription={count > 1 ? "carousel" : undefined}
              aria-label={count > 1 ? `${product.name} photos, ${activeIndex + 1} of ${count}. Swipe or use arrow keys.` : undefined}
            >
              <img src={activeImage} alt={`${product.name} — view ${activeIndex + 1}`} draggable={false} />
              {count > 1 && (
                <>
                  <button
                    type="button"
                    className="product-detail__arrow product-detail__arrow--prev"
                    onClick={() => step(-1)}
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="product-detail__arrow product-detail__arrow--next"
                    onClick={() => step(1)}
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                  <span className="product-detail__counter" aria-hidden="true">
                    {activeIndex + 1} / {count}
                  </span>
                </>
              )}
            </div>
            {count > 1 && (
              <div className="product-detail__thumbs" role="list" aria-label="Product photos" ref={thumbsRef}>
                {gallery.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    role="listitem"
                    className={`product-detail__thumb${i === activeIndex ? " is-active" : ""}`}
                    onClick={() => setActiveIndex(i)}
                    aria-label={`Show photo ${i + 1}`}
                    aria-pressed={i === activeIndex}
                  >
                    <img src={thumbSrc(src)} alt="" width="240" height="240" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="product-detail__info reveal visible">
            <p className="product-detail__eyebrow">{product.icon} Handcrafted resin</p>
            <h1 className="product-detail__title">{product.name}</h1>
            <div className="section-divider product-detail__divider" />
            <p className="product-detail__lede">{product.description}</p>
            {product.detailDescription && (
              <p className="product-detail__body">{product.detailDescription}</p>
            )}
            {product.highlights?.length > 0 && (
              <ul className="product-detail__highlights">
                {product.highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
            <div className="product-detail__actions">
              <a href="/#custom" className="btn-primary" data-custom-order="true" data-product={product.name}>
                <span>Enquire about this piece</span>
              </a>
              <Link to="/collection" className="btn-enquire">
                Browse more
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
